import React from 'react';
import { ArrowLeft, Trash2, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const JustSolarPrivacyPolicy = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-container">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
        
        <div className="privacy-policy-content">
          <h1>JustSolar Privacy Policy</h1>
          <p className="last-updated">Last updated: October 16, 2026</p>
          
          <section>
            <p>
              This privacy policy applies to the <strong>JustSolar</strong> Android application (hereby referred to as "Application") developed and operated by <strong>Pankaj Acharya</strong>, trading as <strong>JustLabs</strong> ("Service Provider", "we", "us", or "our"). JustSolar is a specialized commercial Android software application engineered for solar EPC companies, rooftop installers, and project engineers.
            </p>
          </section>

          <section>
            <h2>1. Information Collection and Storage</h2>
            <p>
              JustSolar is built with an <strong>offline-first</strong> philosophy to protect proprietary business data and client information:
            </p>
            <ul>
              <li>
                <strong>Project & Solar Design Data:</strong> All site survey parameters, rooftop dimensions, shading measurements, Bill of Materials (BOM) configurations, customer pricing tables, and proposal drafts are stored <strong>locally on your Android device</strong>. We do not transmit or store your proprietary customer quotes on our external servers.
              </li>
              <li>
                <strong>Device & License Information:</strong> When you activate your JustSolar lifetime license, the Application may securely transmit a unique device identifier (UUID/Android ID) along with your issued license key to verify license validity and prevent unauthorized copying.
              </li>
              <li>
                <strong>Camera & Storage Permissions:</strong> The Application requests access to your device camera and local storage solely to capture rooftop photos, site survey documentation, and save exported PDF quotations.
              </li>
              <li>
                <strong>Location Data (GPS):</strong> Location permission may be requested solely on-device to determine the latitude, longitude, solar tilt angle, and solar irradiance calculations for site survey accuracy. This location data remains on your device.
              </li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Information</h2>
            <p>The information processed by JustSolar is used strictly for:</p>
            <ul>
              <li>Executing solar PV engineering sizing and calculations on-device.</li>
              <li>Compiling customer quotations and Bill of Materials (BOM) PDFs.</li>
              <li>Validating and managing your purchased single-device lifetime license.</li>
              <li>Providing dedicated technical support, bug fixes, and feature updates.</li>
            </ul>
            <p>
              <strong>We never sell, rent, or trade your business data, project records, or customer contact details to third parties or advertisers.</strong>
            </p>
          </section>

          <section>
            <h2>3. Third-Party Services & Payment Processing</h2>
            <p>
              JustSolar software product licenses are purchased online via authorized, PCI-DSS compliant payment gateways. 
            </p>
            <ul>
              <li>Payment details (UPI, debit/credit cards, net banking credentials) are collected and processed directly by our secure payment gateway partner via 128-bit/256-bit SSL encryption.</li>
              <li>JustLabs does not store or access your sensitive payment card details or banking passwords.</li>
            </ul>
          </section>

          <section>
            <h2>4. Security</h2>
            <p>
              We prioritize safeguarding your information. Since project calculations and exported proposals are kept on your physical device, your data is protected by your Android device's native sandboxing and encryption mechanisms. We recommend keeping your device protected with a PIN, password, or biometric lock.
            </p>
          </section>

          <section id="data-deletion">
            <h2>5. User Data Deletion Policy & Requests</h2>
            <p>
              JustSolar is built offline-first; all your customer quotations, Bill of Materials (BOM), and site survey records are stored securely on your local device. In addition, for any customer support interactions, email communications, or license registry records, you have the right to request full and permanent deletion.
            </p>
            <div className="data-deletion-card">
              <div className="data-deletion-header">
                <Trash2 size={24} color="#dc2626" />
                <h3>How to Request JustSolar Data Deletion</h3>
              </div>
              <p>
                To request permanent deletion of your data or licensing records, please email our support team at:
              </p>
              <p style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </p>
              <p>
                Please include your registered email address, company name, or Payment Transaction ID. We will permanently delete your records from our systems within <strong>48 to 72 business hours</strong> and confirm via email.
              </p>
              <a 
                href="mailto:pankajacharyaorg@gmail.com?subject=JustSolar%20Data%20Deletion%20Request&body=Hello%20JustLabs%20Support%2C%0A%0AI%20would%20like%20to%20request%20the%20permanent%20deletion%20of%20all%20my%20data%20and%20records%20associated%20with%20JustSolar.%0A%0AMy%20details%3A%0A-%20Full%20Name%20%2F%20Company%3A%20%0A-%20Registered%20Email%20Address%3A%20%0A-%20License%20Key%20or%20Payment%20Transaction%20ID%20(if%20any)%3A%20%0A-%20Additional%20Notes%3A%20%0A%0AThank%20you."
                className="data-deletion-btn"
              >
                <Send size={16} />
                Send Data Deletion Email
              </a>
              <div className="data-deletion-email-direct">
                Direct Contact: <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </div>
            </div>
          </section>

          <section>
            <h2>6. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We encourage users to periodically review this page for any changes. Continued use of the Application after revisions constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>7. Contact Us & Grievance Redressal</h2>
            <div className="contact-info">
              <p>For any questions or concerns regarding this JustSolar Privacy Policy, please contact:</p>
              <p><strong>Legal Entity:</strong> Pankaj Acharya (Sole Proprietor, JustLabs)</p>
              <p><strong>Email:</strong> <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a></p>
              <p><strong>Support Hours:</strong> Monday – Saturday, 9:30 AM to 6:30 PM IST</p>
              <p><strong>Jurisdiction:</strong> Ajmer, Rajasthan, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default JustSolarPrivacyPolicy;
